import { getSessionToken } from './sessionTokenService';

export interface SubscriptionDetail {
  status: string;
  planType: string;
  startAt: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialStartAt: string | null;
  trialEndAt: string | null;
  lastPaidAt: string | null;
  firstPaidAt: string | null;
  cancelAtPeriodEnd: string | null;
  isOnTrial: boolean;
  nextBillingDate: string | null;
  isEligibleFor48HourFullRefund: boolean;
  fullRefundWindowEndsAt: string | null;
}

export interface GetMySubscriptionResponse {
  subscription: SubscriptionDetail | null;
}

export interface SubscriptionConfig {
  clientId: string;
  planId: string;
}

class SubscriptionApiService {
  private baseUrl = `${process.env.REACT_APP_API_URL || ''}/api/v1/subscription`;

  private async makeRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No access token found');
    }

    const sessionToken = getSessionToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers as Record<string, string>),
    };
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getConfig(): Promise<SubscriptionConfig> {
    return this.makeRequest<SubscriptionConfig>(`${this.baseUrl}/config`);
  }

  async getMySubscription(): Promise<GetMySubscriptionResponse> {
    return this.makeRequest<GetMySubscriptionResponse>(`${this.baseUrl}/me`);
  }

  async cancelSubscription(cancelAtPeriodEnd: boolean): Promise<{ ok: boolean }> {
    return this.makeRequest<{ ok: boolean }>(`${this.baseUrl}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ cancelAtPeriodEnd }),
    });
  }
}

export const subscriptionApi = new SubscriptionApiService();
